import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRunning,
  faUsers,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "@cakrawalajakarta/shared-components";
import { db } from "@lib/firebase";
import { SectionShell } from "../components/home/SectionShell";
import { SectionTitle } from "../components/home/SectionTitle";

const EVENT_ID = "ULTRA24";

const formatPrice = (value) => {
  const num = Number(value || 0);
  return `Rp ${num.toLocaleString("id-ID")}`;
};

const slugifyCategory = (name = "") =>
  String(name)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const isFunRun = (categoryName = "") => {
  const n = String(categoryName).toUpperCase();
  return n.includes("FUN RUN") || n.includes("FUNRUN");
};

const getCategoryMeta = (categoryName = "") => {
  const n = String(categoryName).toUpperCase();
  if (n.includes("RELAY") || n.includes("GROUP") || n.includes("TEAM")) {
    return { icon: faUsers, tag: "Man / Woman / Mix" };
  }
  if (isFunRun(n)) {
    return { icon: faRunning, tag: "All Ages" };
  }
  return { icon: faRunning, tag: "Man / Woman" };
};

const getSubtitle = (category = {}) => {
  const name = String(category.categoryName || "").toUpperCase();
  if (name.includes("RELAY")) return "24 Hours Total";
  if (name.includes("24H")) return "24 Hours";
  if (name.includes("12H")) return "12 Hours";
  if (name.includes("6H")) return "6 Hours";
  if (isFunRun(name)) return `${category.distance || ""} KM Fun Run`;
  if (Number(category.distance) > 0) return `Target ${category.distance}K`;
  if (Number(category.cot) > 0) return `${category.cot} Hours`;
  return "Race Category";
};

const getWatermark = (cat) => {
  const name = String(cat.categoryName || "").toUpperCase();
  if (name.includes("RELAY")) return "4×6";
  if (Number(cat.cot) > 0 && Number(cat.distance) === 0) return String(cat.cot);
  if (Number(cat.distance) > 0) return String(cat.distance);
  return "";
};

export const Ultra24CategorySection = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const snap = await getDoc(doc(db, "semuaKegiatan", EVENT_ID));
        if (!snap.exists()) {
          setCategories([]);
          return;
        }
        const data = snap.data() || {};
        const raw = Array.isArray(data.categories) ? data.categories : [];
        setCategories(raw.filter((c) => c && c.categoryName));
      } catch (error) {
        console.error("[Ultra24CategorySection] Failed to load categories:", error);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const orderedCategories = useMemo(() => {
    const preferredOrder = [
      "24H INDIVIDU",
      "12H INDIVIDU",
      "6H INDIVIDU",
      "100K INDIVIDU",
      "50K INDIVIDU",
      "42K INDIVIDU",
      "RELAY 100K",
      "RELAY 50K",
      "10K FUN RUN",
      "5K FUN RUN",
    ];
    const score = (name = "") => {
      const idx = preferredOrder.indexOf(String(name).toUpperCase());
      return idx === -1 ? 999 : idx;
    };
    return [...categories].sort((a, b) => {
      const byName = score(a.categoryName) - score(b.categoryName);
      if (byName !== 0) return byName;
      return String(a.categoryName).localeCompare(String(b.categoryName));
    });
  }, [categories]);
  const renderCard = (cat, theme = "ultra") => {
    const meta = getCategoryMeta(cat.categoryName);
    const watermark = getWatermark(cat);
    
    const isFunRunTheme = theme === "funrun";
    const containerClass = isFunRunTheme
      ? "relative rounded-2xl overflow-hidden bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-lg hover:shadow-orange-500/20 hover:-translate-y-0.5 transition-all duration-300"
      : "relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-800/80 hover:border-red-500/40 hover:bg-slate-850 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300";
    
    const watermarkClass = isFunRunTheme
      ? "absolute right-12 top-1/2 -translate-y-1/2 text-[8rem] font-black leading-none select-none pointer-events-none text-white/[0.08]"
      : "absolute right-12 top-1/2 -translate-y-1/2 text-[8rem] font-black leading-none select-none pointer-events-none text-white/[0.06]";

    return (
      <div key={cat.categoryName} className={containerClass}>
        {watermark && <span className={watermarkClass}>{watermark}</span>}

        <div className="relative p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 z-10">
          
          {/* Column 1: Category Name & Meta Icon */}
          <div className="flex items-center gap-4 md:w-1/4">
            <div className={`flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 ${isFunRunTheme ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300 border border-slate-700"}`}>
              <FontAwesomeIcon icon={meta.icon} className="text-xl" />
            </div>
            <div>
              <h3 className="text-2xl font-black text-white leading-tight">{cat.categoryName}</h3>
              <span className={`text-[10px] font-bold py-0.5 px-2 rounded-full mt-1 inline-block ${isFunRunTheme ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300 border border-slate-700"}`}>
                {meta.tag}
              </span>
            </div>
          </div>

          {/* Column 2: Specs & Duration Details */}
          <div className="flex flex-col gap-2 md:w-1/4">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold ${isFunRunTheme ? "text-orange-100" : "text-slate-400"}`}>Duration / Distance:</span>
              <p className={`text-sm font-bold ${isFunRunTheme ? "text-white" : "text-slate-200"}`}>{getSubtitle(cat)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {Number(cat.cot) > 0 && (
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${isFunRunTheme ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300 border border-slate-700"}`}>
                  COT {cat.cot}H
                </span>
              )}
              {Number(cat.distance) > 0 && (
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${isFunRunTheme ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300 border border-slate-700"}`}>
                  {cat.distance} KM
                </span>
              )}
            </div>
          </div>

          {/* Column 3: Registration Fee */}
          <div className="flex flex-col md:w-1/4">
            <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isFunRunTheme ? "text-orange-200" : "text-slate-500"}`}>Registration Fee</p>
            <span className="text-2xl font-black text-white leading-none">{formatPrice(cat.price)}</span>
          </div>

          {/* Column 4: Action Button */}
          <div className="flex items-center md:justify-end md:w-1/4">
            <Button to={`/ultra24/${slugifyCategory(cat.categoryName)}`} variant="secondary" className="w-full md:w-auto">
              Discover
              <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-xs" />
            </Button>
          </div>

        </div>
      </div>
    );
  };

  const individuCategories = orderedCategories.filter((c) => !isFunRun(c.categoryName) && !c.categoryName.toUpperCase().includes("RELAY"));
  const relayCategories = orderedCategories.filter((c) => !isFunRun(c.categoryName) && c.categoryName.toUpperCase().includes("RELAY"));
  const funRunCategories = orderedCategories.filter((c) => isFunRun(c.categoryName));

  return (
    <SectionShell id="running-category" className="bg-white">
      <SectionTitle
        title="Choose Your Challenge"
        subtitle="Various race categories are available for beginner to professional ultra runners at the Patriot Candrabhaga Stadium track."
        align="center"
      />

      {loading ? (
        <div className="text-center text-slate-500 py-8">Loading categories...</div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Ultra Individu categories */}
          {individuCategories.length > 0 && (
            <div className="flex flex-col gap-4">
              {individuCategories.map((cat) => renderCard(cat, "ultra"))}
            </div>
          )}

          {/* Ultra Relay categories */}
          {relayCategories.length > 0 && (
            <div className="flex flex-col gap-4">
              {relayCategories.map((cat) => renderCard(cat, "ultra"))}
            </div>
          )}

          {/* Fun Run categories */}
          {funRunCategories.length > 0 && (
            <div className="flex flex-col gap-4">
              {funRunCategories.map((cat) => renderCard(cat, "funrun"))}
            </div>
          )}
        </div>
      )}
    </SectionShell>
  );
};
